import React from 'react'
import { ControlledDto } from 'src/Libs/flow/installation/api'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { parseModbusTcpChargingStation } from 'src/Libs/flow/parsers'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import useModals from 'src/Hooks/useModals'
import { SettingsBox } from 'src/Elements/Data'
import { Box, Button, Typography } from '@mui/material'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
/** Map of enum to translation key. */
const phaseManagementText: Record<NonNullable<ControlledDto['parameters']['phasesManagement']>, string> = {
  '1PhBlocked': 'installation.evseEditor.phaseManagement.1PhBlocked',
  '3PhBlocked': 'installation.evseEditor.phaseManagement.3PhBlocked',
  AutoPhaseSwitch: 'installation.evseEditor.phaseManagement.autoPhaseSwitch',
}

interface AdvancedEvseSettingsProps {
  installationId: number
  queryIsLoading: boolean
  evse: Nullable<ControlledDto>
  isSinglePhase: boolean
}

/** The advanced settings page of an installation's (user-level) charging station settings. */
export const AdvancedEvseSettings: React.FC<AdvancedEvseSettingsProps> = ({ installationId, queryIsLoading, evse, isSinglePhase }) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()
  const { openHoldingTimeEditorDialog, openPhaseManagementEditorDialog } = useModals()

  const handleOpenPhaseManagementEditorDialog = (): void => {
    openPhaseManagementEditorDialog({ installationId, queryIsLoading, evse: parseModbusTcpChargingStation(evse), isSinglePhase })
  }
  const handleOpenHoldingTimeEditorDialog = (): void => {
    openHoldingTimeEditorDialog({ installationId, queryIsLoading, evse })
  }

  const isModbusTcp = evse.media === 'ModbusTCP'

  return (
    <AccordionItem title={t('user-settings.evse-page.advanced-settings')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      {isModbusTcp && (
        <SettingsBox
          title={t('user-settings.evse-advanced-page.options.phase-management')}
          description={
            <Box py={1}>
              <Typography variant="body2" fontWeight={'bold'} sx={{ color: 'grey[900]' }}>
                {evse.parameters?.phasesManagement ? t(phaseManagementText[evse.parameters?.phasesManagement]) : ''}
              </Typography>
              <Typography variant="body2">{t('user-settings.evse-advanced-page.options.phase-management-description')}</Typography>
            </Box>
          }
          suffix={
            <Button
              variant="contained"
              sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
              onClick={handleOpenPhaseManagementEditorDialog}
              disabled={!userCanEdit}
            >
              {t('common.edit')}
            </Button>
          }
        />
      )}

      <SettingsBox
        title={t('user-settings.evse-advanced-page.options.holding-time')}
        description={
          <Box py={1}>
            <Typography variant="body2" fontWeight={'bold'} sx={{ color: 'grey[900]' }}>
              {`${evse.parameters?.sunMode?.holdingTimeInMin || ''} ${t('affixes.minute')}`}
            </Typography>
            <Typography variant="body2">{t('user-settings.evse-advanced-page.options.holding-time-description')}</Typography>
          </Box>
        }
        suffix={
          <Button
            variant="contained"
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            onClick={handleOpenHoldingTimeEditorDialog}
            disabled={!userCanEdit}
          >
            {t('common.edit')}
          </Button>
        }
      />
    </AccordionItem>
  )
}
